import React, { useState } from 'react';
import {
  QRDesign,
  DotType,
  CornerSquareType,
  CornerDotType,
  ErrorCorrectionLevel,
  FrameType
} from '@/types/qr';
import { Slider } from '@/components/ui/Slider';
import { Input } from '@/components/ui/Input';
import { sanitizeSvgString } from '@/lib/security/sanitizer';
import { useToast } from '@/components/ui/Toast';
import {
  Palette,
  Shapes,
  Eye,
  Image as ImageIcon,
  Frame,
  Sliders,
  Upload,
  Trash2
} from 'lucide-react';

interface DesignEditorProps {
  design: QRDesign;
  onChange: (design: QRDesign) => void;
}

export const DesignEditor: React.FC<DesignEditorProps> = ({ design, onChange }) => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'colors' | 'shapes' | 'logo' | 'frames'>('colors');

  // Curated Apple Color Palettes
  const colorPalettes = [
    { name: 'Obsidian Ink', fg: '#1d1d1f', bg: '#ffffff' },
    { name: 'Action Blue', fg: '#0066cc', bg: '#f5f5f7' },
    { name: 'Space Gray', fg: '#333336', bg: '#fafafc' },
    { name: 'Midnight', fg: '#ffffff', bg: '#1d1d1f' },
    { name: 'Forest Eco', fg: '#065f46', bg: '#f5f5f7' },
    { name: 'Product Red', fg: '#e03e3e', bg: '#fafafc' }
  ];

  // Handle Logo Upload (PNG, JPEG, WebP, SVG)
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showToast('error', 'File Too Large', 'Please upload a logo image under 2MB.');
      return;
    }

    const reader = new FileReader();

    if (file.type === 'image/svg+xml') {
      reader.onload = () => {
        const rawSvg = reader.result as string;
        const cleanSvg = sanitizeSvgString(rawSvg);
        if (!cleanSvg) {
          showToast('error', 'Invalid SVG', 'Could not sanitize SVG image.');
          return;
        }
        const svgBlob = new Blob([cleanSvg], { type: 'image/svg+xml' });
        const cleanUrl = URL.createObjectURL(svgBlob);
        onChange({
          ...design,
          errorCorrection: 'H',
          logo: {
            dataUrl: cleanUrl,
            size: design.logo?.size || 0.24,
            margin: design.logo?.margin || 4,
            shape: 'rounded',
            hideBackgroundDots: true
          }
        });
        showToast('info', 'Logo Attached', 'Error correction automatically switched to H (30%) for maximum scan reliability.');
      };
      reader.readAsText(file);
    } else {
      reader.onload = () => {
        onChange({
          ...design,
          errorCorrection: 'H',
          logo: {
            dataUrl: reader.result as string,
            size: design.logo?.size || 0.24,
            margin: design.logo?.margin || 4,
            shape: 'rounded',
            hideBackgroundDots: true
          }
        });
        showToast('info', 'Logo Attached', 'Error correction automatically switched to H (30%) for maximum scan reliability.');
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    onChange({
      ...design,
      logo: undefined
    });
  };

  return (
    <div className="w-full space-y-5">
      {/* Design Sub-tabs Navigation (Parchment Capsule Row) */}
      <div className="flex items-center gap-1.5 p-1 bg-muted rounded-full overflow-x-auto">
        {[
          { id: 'colors', label: 'Colors', icon: <Palette className="h-3.5 w-3.5" /> },
          { id: 'shapes', label: 'Modules', icon: <Shapes className="h-3.5 w-3.5" /> },
          { id: 'logo', label: 'Logo', icon: <ImageIcon className="h-3.5 w-3.5" /> },
          { id: 'frames', label: 'Frames', icon: <Frame className="h-3.5 w-3.5" /> }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-normal tracking-tight transition-all whitespace-nowrap cursor-pointer ${
              activeTab === tab.id
                ? 'bg-card text-primary font-medium shadow-xs'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ----------------- TAB: COLORS ----------------- */}
      {activeTab === 'colors' && (
        <div className="space-y-5 animate-in fade-in duration-150">
          {/* Quick Palettes */}
          <div>
            <label className="block text-[12px] font-normal tracking-tight text-muted-foreground mb-2">
              Curated Color Presets
            </label>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {colorPalettes.map((p) => (
                <button
                  key={p.name}
                  onClick={() =>
                    onChange({
                      ...design,
                      dotsColor: p.fg,
                      cornersSquareColor: p.fg,
                      cornersDotColor: p.fg,
                      backgroundColor: p.bg,
                      isTransparent: false,
                      dotsGradient: undefined
                    })
                  }
                  className="flex flex-col items-center p-2 rounded-xl border border-border hover:border-primary bg-card cursor-pointer transition-all active:scale-95"
                >
                  <div className="flex w-full h-5 rounded-md overflow-hidden border border-black/10">
                    <div className="w-1/2" style={{ backgroundColor: p.fg }} />
                    <div className="w-1/2" style={{ backgroundColor: p.bg }} />
                  </div>
                  <span className="text-[10px] font-normal text-muted-foreground mt-1 truncate">
                    {p.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Color Pickers */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 rounded-2xl bg-card border border-border space-y-3">
              <label className="block text-[12px] font-normal tracking-tight text-muted-foreground">
                Foreground (Modules)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={design.dotsColor}
                  onChange={(e) =>
                    onChange({
                      ...design,
                      dotsColor: e.target.value,
                      cornersSquareColor: e.target.value,
                      cornersDotColor: e.target.value,
                      dotsGradient: undefined
                    })
                  }
                  className="h-10 w-12 rounded-lg cursor-pointer bg-transparent border-0"
                />
                <input
                  type="text"
                  value={design.dotsColor}
                  onChange={(e) =>
                    onChange({
                      ...design,
                      dotsColor: e.target.value,
                      cornersSquareColor: e.target.value,
                      cornersDotColor: e.target.value,
                      dotsGradient: undefined
                    })
                  }
                  className="font-mono text-[12px] px-3 py-2 rounded-lg bg-muted border border-border uppercase"
                />
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-card border border-border space-y-3">
              <div className="flex justify-between items-center">
                <label className="block text-[12px] font-normal tracking-tight text-muted-foreground">
                  Background Canvas
                </label>
                <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={design.isTransparent}
                    onChange={(e) => onChange({ ...design, isTransparent: e.target.checked })}
                    className="rounded border-input text-primary focus:ring-ring"
                  />
                  <span>Transparent</span>
                </label>
              </div>

              {!design.isTransparent ? (
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={design.backgroundColor}
                    onChange={(e) => onChange({ ...design, backgroundColor: e.target.value })}
                    className="h-10 w-12 rounded-lg cursor-pointer bg-transparent border-0"
                  />
                  <input
                    type="text"
                    value={design.backgroundColor}
                    onChange={(e) => onChange({ ...design, backgroundColor: e.target.value })}
                    className="font-mono text-[12px] px-3 py-2 rounded-lg bg-muted border border-border uppercase"
                  />
                </div>
              ) : (
                <p className="text-[12px] text-muted-foreground italic py-2">
                  Transparent background enabled (PNG / SVG output)
                </p>
              )}
            </div>
          </div>

          {/* Advanced: error correction + quiet zone, collapsed by default */}
          <details className="group rounded-2xl border border-border overflow-hidden">
            <summary className="flex items-center gap-1.5 px-4 py-3 text-[13px] font-medium text-muted-foreground cursor-pointer list-none [&::-webkit-details-marker]:hidden bg-card">
              <Sliders className="h-3.5 w-3.5" />
              <span>Advanced (error correction & quiet zone)</span>
            </summary>
            <div className="p-4 pt-2 space-y-5 bg-card">
              {/* Error Correction Level */}
              <div className="space-y-2.5">
                <label className="block text-[12px] font-normal tracking-tight text-muted-foreground">
                  Reed-Solomon Error Correction Level
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {[
                    { id: 'L', label: 'Level L', cap: '~7%', desc: 'Light density' },
                    { id: 'M', label: 'Level M', cap: '~15%', desc: 'Standard' },
                    { id: 'Q', label: 'Level Q', cap: '~25%', desc: 'High recovery' },
                    { id: 'H', label: 'Level H', cap: '~30%', desc: 'Best with logos' }
                  ].map((ec) => (
                    <button
                      key={ec.id}
                      onClick={() =>
                        onChange({ ...design, errorCorrection: ec.id as ErrorCorrectionLevel })
                      }
                      className={`p-3 rounded-xl border text-left cursor-pointer transition-all active:scale-95 ${
                        design.errorCorrection === ec.id
                          ? 'bg-primary/5 border-primary ring-1 ring-primary'
                          : 'bg-card border-border hover:border-muted-foreground'
                      }`}
                    >
                      <span className="text-[14px] font-semibold text-foreground block">
                        {ec.label}
                      </span>
                      <span className="text-[12px] text-primary block mt-0.5">
                        {ec.cap}
                      </span>
                      <span className="text-[11px] text-muted-foreground block mt-0.5">{ec.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Quiet Zone Margin Slider */}
              <div className="p-4 rounded-2xl bg-muted border border-border">
                <Slider
                  label="Quiet Zone Margin"
                  min={0}
                  max={32}
                  step={2}
                  value={design.margin}
                  valueDisplay={`${design.margin}px`}
                  onChange={(e) => onChange({ ...design, margin: Number(e.target.value) })}
                />
                <p className="text-[12px] text-muted-foreground mt-2">
                  Recommended minimum quiet zone is 10px so optical sensors recognize boundaries.
                </p>
              </div>
            </div>
          </details>
        </div>
      )}

      {/* ----------------- TAB: MODULE SHAPES ----------------- */}
      {activeTab === 'shapes' && (
        <div className="space-y-4 animate-in fade-in duration-150">
          <label className="block text-[12px] font-normal tracking-tight text-muted-foreground">
            Module Pattern Style
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { id: 'square', label: 'Classic Square', desc: 'Standard high-density' },
              { id: 'rounded', label: 'Soft Rounded', desc: 'Smooth modern curves' },
              { id: 'dots', label: 'Pure Dots', desc: 'Circular micro-dots' },
              { id: 'classy', label: 'Classy Diamond', desc: 'Diamond-cut corners' },
              { id: 'classy-rounded', label: 'Classy Rounded', desc: 'Fluid curved shape' },
              { id: 'extra-rounded', label: 'Pill Rounded', desc: 'Capsule pill modules' }
            ].map((shape) => {
              const isSelected = design.dotsType === shape.id;
              return (
                <button
                  key={shape.id}
                  onClick={() => onChange({ ...design, dotsType: shape.id as DotType })}
                  className={`flex flex-col text-left p-3.5 rounded-2xl border transition-all cursor-pointer active:scale-95 ${
                    isSelected
                      ? 'bg-primary/5 border-primary ring-1 ring-primary'
                      : 'bg-card border-border hover:border-muted-foreground'
                  }`}
                >
                  <span className="text-[14px] font-semibold text-foreground tracking-tight">{shape.label}</span>
                  <span className="text-[12px] text-muted-foreground mt-0.5">{shape.desc}</span>
                </button>
              );
            })}
          </div>

          {/* Finders: outer/inner finder shapes, collapsed by default */}
          <details className="group rounded-2xl border border-border overflow-hidden">
            <summary className="flex items-center gap-1.5 px-4 py-3 text-[13px] font-medium text-muted-foreground cursor-pointer list-none [&::-webkit-details-marker]:hidden bg-card">
              <Eye className="h-3.5 w-3.5" />
              <span>Advanced shape (finder corners)</span>
            </summary>
            <div className="p-4 pt-2 space-y-5 bg-card">
              {/* Outer Finder Corner */}
              <div className="space-y-3">
                <label className="block text-[12px] font-normal tracking-tight text-muted-foreground">
                  Outer Finder Corner Style
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'square', label: 'Square Box' },
                    { id: 'extra-rounded', label: 'Rounded Box' },
                    { id: 'dot', label: 'Circular Eye' }
                  ].map((cs) => (
                    <button
                      key={cs.id}
                      onClick={() =>
                        onChange({ ...design, cornersSquareType: cs.id as CornerSquareType })
                      }
                      className={`p-3 rounded-xl border text-[14px] font-normal text-center cursor-pointer transition-all active:scale-95 ${
                        design.cornersSquareType === cs.id
                          ? 'bg-primary/5 border-primary text-primary font-medium'
                          : 'bg-card border-border hover:border-muted-foreground text-foreground'
                      }`}
                    >
                      {cs.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Inner Finder Dot */}
              <div className="space-y-3">
                <label className="block text-[12px] font-normal tracking-tight text-muted-foreground">
                  Inner Finder Center Dot
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: 'square', label: 'Square Pupil' },
                    { id: 'dot', label: 'Circular Pupil' }
                  ].map((cd) => (
                    <button
                      key={cd.id}
                      onClick={() => onChange({ ...design, cornersDotType: cd.id as CornerDotType })}
                      className={`p-3 rounded-xl border text-[14px] font-normal text-center cursor-pointer transition-all active:scale-95 ${
                        design.cornersDotType === cd.id
                          ? 'bg-primary/5 border-primary text-primary font-medium'
                          : 'bg-card border-border hover:border-muted-foreground text-foreground'
                      }`}
                    >
                      {cd.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </details>
        </div>
      )}

      {/* ----------------- TAB: LOGO ----------------- */}
      {activeTab === 'logo' && (
        <div className="space-y-5 animate-in fade-in duration-150">
          {!design.logo?.dataUrl ? (
            <label className="border-2 border-dashed border-border rounded-2xl p-8 flex flex-col items-center justify-center gap-3 bg-card hover:bg-muted cursor-pointer transition-all">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={handleLogoUpload}
                className="hidden"
              />
              <div className="p-3 rounded-full bg-muted text-primary">
                <Upload className="h-6 w-6" />
              </div>
              <div className="text-center">
                <p className="text-[14px] font-semibold text-foreground tracking-tight">
                  Upload Logo or Brand Mark
                </p>
                <p className="text-[12px] text-muted-foreground mt-0.5">
                  PNG, JPEG, WebP, or SVG (Max 2MB)
                </p>
              </div>
            </label>
          ) : (
            <div className="p-4 rounded-2xl bg-card border border-border space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src={design.logo.dataUrl}
                    alt="Logo preview"
                    className="h-12 w-12 object-contain rounded-lg bg-muted p-1 border border-border"
                  />
                  <div>
                    <h5 className="text-[14px] font-semibold text-foreground">
                      Active Logo
                    </h5>
                    <p className="text-[12px] text-success font-normal">
                      ✓ Error Correction H Enabled
                    </p>
                  </div>
                </div>
                <button
                  onClick={removeLogo}
                  className="p-2 text-destructive hover:bg-muted rounded-full transition-colors cursor-pointer"
                  title="Remove logo"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <Slider
                label="Logo Scale Size"
                min={0.15}
                max={0.35}
                step={0.02}
                value={design.logo.size}
                valueDisplay={`${Math.round(design.logo.size * 100)}%`}
                onChange={(e) =>
                  onChange({
                    ...design,
                    logo: { ...design.logo!, size: Number(e.target.value) }
                  })
                }
              />

              <Slider
                label="Logo Margin Padding"
                min={0}
                max={12}
                step={1}
                value={design.logo.margin}
                valueDisplay={`${design.logo.margin}px`}
                onChange={(e) =>
                  onChange({
                    ...design,
                    logo: { ...design.logo!, margin: Number(e.target.value) }
                  })
                }
              />
            </div>
          )}
        </div>
      )}

      {/* ----------------- TAB: FRAMES ----------------- */}
      {activeTab === 'frames' && (
        <div className="space-y-5 animate-in fade-in duration-150">
          <label className="block text-[12px] font-normal tracking-tight text-muted-foreground">
            Callout Frame Style
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { id: 'none', label: 'No Frame', desc: 'Pure museum QR' },
              { id: 'bottom-badge', label: 'Bottom Badge', desc: '"SCAN ME" pill' },
              { id: 'top-badge', label: 'Top Header', desc: 'Banner headline' },
              { id: 'card-polaroid', label: 'Polaroid Card', desc: 'Card with caption' }
            ].map((f) => {
              const isSelected = (design.frame?.type || 'none') === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() =>
                    onChange({
                      ...design,
                      frame: {
                        type: f.id as FrameType,
                        text: design.frame?.text || 'SCAN ME',
                        subtext: design.frame?.subtext || 'Point phone camera to scan',
                        textColor: design.frame?.textColor || '#0066cc',
                        backgroundColor: design.frame?.backgroundColor || '#ffffff'
                      }
                    })
                  }
                  className={`flex flex-col text-left p-3.5 rounded-2xl border transition-all cursor-pointer active:scale-95 ${
                    isSelected
                      ? 'bg-primary/5 border-primary ring-1 ring-primary'
                      : 'bg-card border-border hover:border-muted-foreground'
                  }`}
                >
                  <span className="text-[14px] font-semibold text-foreground tracking-tight">{f.label}</span>
                  <span className="text-[12px] text-muted-foreground mt-0.5">{f.desc}</span>
                </button>
              );
            })}
          </div>

          {design.frame && design.frame.type !== 'none' && (
            <div className="p-4 rounded-2xl bg-card border border-border space-y-4">
              <Input
                label="Callout Text"
                value={design.frame.text}
                onChange={(e) =>
                  onChange({
                    ...design,
                    frame: { ...design.frame!, text: e.target.value }
                  })
                }
                placeholder="SCAN ME / JOIN WI-FI / PAY NOW"
              />

              {design.frame.type === 'card-polaroid' && (
                <Input
                  label="Subtitle Text"
                  value={design.frame.subtext || ''}
                  onChange={(e) =>
                    onChange({
                      ...design,
                      frame: { ...design.frame!, subtext: e.target.value }
                    })
                  }
                  placeholder="Scan with your smartphone camera"
                />
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] font-normal tracking-tight text-muted-foreground mb-1.5">
                    Frame Accent
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={design.frame.textColor}
                      onChange={(e) =>
                        onChange({
                          ...design,
                          frame: { ...design.frame!, textColor: e.target.value }
                        })
                      }
                      className="h-9 w-10 rounded-lg cursor-pointer bg-transparent border-0"
                    />
                    <span className="font-mono text-[12px] text-muted-foreground uppercase">
                      {design.frame.textColor}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-[12px] font-normal tracking-tight text-muted-foreground mb-1.5">
                    Card Surface
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={design.frame.backgroundColor}
                      onChange={(e) =>
                        onChange({
                          ...design,
                          frame: { ...design.frame!, backgroundColor: e.target.value }
                        })
                      }
                      className="h-9 w-10 rounded-lg cursor-pointer bg-transparent border-0"
                    />
                    <span className="font-mono text-[12px] text-muted-foreground uppercase">
                      {design.frame.backgroundColor}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
};
